import { BoxProps, Column, Box, Row } from "@hightouch/htui";
import { usePulseStaggerAnimation } from "./utils";

type BarChartLoaderProps = BoxProps & {
  rows?: number;
};

/**
 * Loader animation for bar charts.
 * Staggered pulse effect in a diagonal pattern.
 * Diagonal effect is achieved through a combination of a left-to-right pulse with a top-to-bottom opacity fade.
 */
export const BarChartLoader = ({ rows = 6, ...props }: BarChartLoaderProps) => {
  return (
    <Column gap={2} w="100%" {...props}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <BarRow key={`${rowIndex}`} index={rowIndex} />
      ))}
    </Column>
  );
};

const scaleIncrement = 2;
const opacityIncrement = 12;

function BarRow({ index }: { index: number }) {
  const { className, scope } = usePulseStaggerAnimation({ duration: 1.125 });

  return (
    <Row
      ref={scope}
      align="center"
      justify="Space-between"
      gap={12}
      pos="relative"
      h={7}
      w={`calc(100% - ${index} * ${scaleIncrement}%)`}
      opacity={`calc(100% - ${index} * ${opacityIncrement}%)`}
    >
      <TableCell className={className} width="10%" />
      <Row gap={2} flex={1}>
        <TableCell className={className} flex={1} />
        <TableCell className={className} width="6%" />
      </Row>
    </Row>
  );
}

const TableCell = ({
  width = "auto",
  flex,
  className,
}: Pick<BoxProps, "className" | "width" | "flex">) => {
  return (
    <Box
      className={className}
      width={width}
      flex={flex}
      height={4}
      bg="gray.200"
      borderRadius="md"
    />
  );
};
